import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Repeat, Pause, Play, Trash2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${window.__BACKEND_URL__}/api`;
const FREQ = [['daily', 'Every day'], ['weekly', 'Every week'], ['biweekly', 'Every 2 weeks'], ['monthly', 'Every month'], ['quarterly', 'Every quarter'], ['yearly', 'Every year']];
const freqLabel = (f) => FREQ.find(([k]) => k === f)?.[1] || f;
const sel = 'w-full h-9 rounded-md px-2 text-sm bg-[#0B1321] border border-[#2D3748] text-white';

// Mirror of backend recurring_invoices.advance(): next service date, keeping the start day-of-month as anchor.
export const advanceDate = (iso, frequency, anchorDay) => {
  const [y, m, d] = iso.split('-').map(Number);
  const pad = (n) => String(n).padStart(2, '0');
  const days = { daily: 1, weekly: 7, biweekly: 14 }[frequency];
  if (days) { const dt = new Date(Date.UTC(y, m - 1, d + days)); return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`; }
  const months = { monthly: 1, quarterly: 3, yearly: 12 }[frequency] || 1;
  const total = (m - 1) + months; const ny = y + Math.floor(total / 12); const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${ny}-${pad(nm)}-${pad(Math.min(anchorDay || d, last))}`;
};
export const nextRuns = (start, frequency, n = 3) => {
  if (!start) return [];
  const anchor = Number(start.slice(-2)); const out = [start];
  while (out.length < n) out.push(advanceDate(out[out.length - 1], frequency, anchor));
  return out;
};

// "Repeat…" dialog: turn a generated Daily Site Invoice into a recurring schedule.
export const RepeatInvoiceDialog = ({ inv, token, onClose, onCreated, kind = 'daily' }) => {
  const [frequency, setFrequency] = useState('weekly');
  const [startDate, setStartDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [createAs, setCreateAs] = useState('draft');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true); setError('');
    try {
      const url = kind === 'job' ? `${API}/jobs/${inv.id}/recurring-invoice` : `${API}/daily-invoices/${inv.id}/recurring`;
      const { data } = await axios.post(url, { frequency, start_date: startDate, end_date: endDate || null, create_as: createAs },
        { headers: { Authorization: `Bearer ${token}` } });
      onCreated?.(data);
    } catch (e) { setError(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!inv} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-[#0F1923] border-[#2D3748] text-white" data-testid="repeat-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Repeat className="w-4 h-4 text-[#1E88E5]" /> Repeat {inv?.invoice_number || inv?.job_number || inv?.title}</DialogTitle>
          <DialogDescription className="text-slate-400">{kind === 'job'
            ? 'HinesTime will create a new completed job copy (same customer, title, rates, hours, materials and crew) with a new job number on each run and email the admins to review and send the invoice.'
            : 'HinesTime will create a copy (same crew, rates, headers, P.O. and template) with the next invoice number on each run and email the admins.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs text-slate-400">Frequency
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className={sel} data-testid="repeat-frequency">{FREQ.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-400">First run (service date)<Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9" data-testid="repeat-start" /></label>
            <label className="block text-xs text-slate-400">Ends (optional)<Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9" data-testid="repeat-end" /></label>
          </div>
          <p className="text-xs text-slate-400" data-testid="repeat-preview">Next runs: <span className="text-white font-mono">{nextRuns(startDate, frequency).filter((d) => !endDate || d <= endDate).join(' · ') || '—'}</span>{endDate && nextRuns(startDate, frequency).some((d) => d > endDate) ? ' (then ends)' : ''}</p>
          {kind !== 'job' && <label className="block text-xs text-slate-400">Create each invoice as
            <select value={createAs} onChange={(e) => setCreateAs(e.target.value)} className={sel} data-testid="repeat-create-as">
              <option value="draft">Draft for review</option>
              <option value="issued">Issued immediately</option>
            </select>
          </label>}
          {error && <p className="text-xs text-red-400" data-testid="repeat-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="repeat-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !startDate} data-testid="repeat-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start schedule'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Recurring schedules list with pause / resume / delete.
export const RecurringSchedulesCard = ({ token, refreshKey, kind = 'daily' }) => {
  const [items, setItems] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => axios.get(`${API}/daily-invoices/recurring`, auth).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { if (token) load(); }, [token, refreshKey]);
  const toggle = async (s) => { await axios.patch(`${API}/daily-invoices/recurring/${s.id}`, { active: !s.active }, auth); load(); };
  const remove = async (s) => { if (!window.confirm(`Delete the ${freqLabel(s.frequency).toLowerCase()} schedule for ${s.source_invoice_number}? Invoices already created stay.`)) return; await axios.delete(`${API}/daily-invoices/recurring/${s.id}`, auth); load(); };
  if (!items || items.length === 0) return null;
  return (
    <div className="ht-card p-4 space-y-2" data-testid="recurring-schedules-card">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Repeat className="w-4 h-4 text-[#1E88E5]" /> {kind === 'job' ? 'Recurring job invoices' : 'Recurring schedules'}</h3>
      <div className="space-y-1.5">
        {items.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm" style={{ background: '#0B1321', border: '1px solid #2D3748' }} data-testid={`recurring-row-${s.id}`}>
            <span className="font-mono text-white">{s.source_invoice_number || s.source_job_number}</span>
            <span className="text-slate-400">{s.customer_name || ''} · {s.job_title || ''}</span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(30,136,229,0.15)', color: '#90CAF9' }}>{freqLabel(s.frequency)}{kind === 'job' ? '' : ` · ${s.create_as === 'issued' ? 'issued' : 'draft'}`}</span>
            <span className="text-xs text-slate-500" data-testid={`recurring-next-${s.id}`}>{s.active ? `next run ${s.next_run}` : 'paused'}{s.end_date ? ` · ends ${s.end_date}` : ''}{s.last_invoice_number || s.last_job_number ? ` · last ${s.last_invoice_number || s.last_job_number}` : ''}</span>
            {s.last_error && <span className="text-xs text-red-400">{s.last_error}</span>}
            <span className="ml-auto flex gap-1.5">
              <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`recurring-toggle-${s.id}`}>{s.active ? <><Pause className="w-3.5 h-3.5 mr-1" />Pause</> : <><Play className="w-3.5 h-3.5 mr-1" />Resume</>}</Button>
              <Button size="sm" variant="outline" onClick={() => remove(s)} data-testid={`recurring-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


// Daily Invoices → "Drafts awaiting review" (recurring + manual drafts) with Issue / Issue all.
export const DraftsQueueCard = ({ token, refreshKey, onIssued }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const load = () => axios.get(`${API}/daily-invoices/drafts`, auth).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { if (token) load(); }, [token, refreshKey]);
  const issue = async (inv) => { setBusy(true); try { await axios.patch(`${API}/daily-invoices/${inv.id}`, { status: 'issued' }, auth); await load(); onIssued?.(); } finally { setBusy(false); } };
  const issueAll = async () => { if (!window.confirm(`Issue all ${items.length} draft invoice${items.length === 1 ? '' : 's'}?`)) return; setBusy(true); try { await axios.post(`${API}/daily-invoices/drafts/issue-all`, {}, auth); await load(); onIssued?.(); } finally { setBusy(false); } };
  if (!items || items.length === 0) return null;
  return (
    <div className="ht-card p-4 space-y-2" data-testid="drafts-queue-card">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-white">Drafts awaiting review <span className="text-slate-500 font-normal">({items.length})</span></h3>
        <Button size="sm" className="ml-auto" onClick={issueAll} disabled={busy} style={{ background: '#1E88E5' }} data-testid="drafts-issue-all">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Issue all'}</Button>
      </div>
      {items.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm" style={{ background: '#0B1321', border: '1px solid #2D3748' }} data-testid={`draft-row-${d.id}`}>
          <span className="font-mono text-white">{d.invoice_number}</span>
          <span className="text-slate-400">{d.bill_to_name || ''} · {d.job_title || ''} · {d.service_date}</span>
          <span className="text-xs text-slate-500">{d.recurring_schedule_id ? 'from schedule' : 'manual'} · {Number(d.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => issue(d)} disabled={busy} data-testid={`draft-issue-${d.id}`}>Issue</Button>
        </div>
      ))}
    </div>
  );
};
